import { TicketSeverity, TicketStatusCode, TicketType } from "@gamedev-web/one-gaming-crm";

import {
  TicketSeverityLabel,
  TicketStatusLabel,
  TicketTypeLabel,
  TranslationMap,
} from "../../models/ticketing";

/**
 * Looks up a translated display value from the translation map.
 *
 * @param map - The translation map (keyed by "entityName:fieldName:sourceKey")
 * @param entityName - The CRM entity name (e.g., "ticket_ticketproduct", "ticket_ticket")
 * @param fieldName - The CRM field name (e.g., "ticket_type", "ticket_severity", "ticket_name")
 * @param sourceKey - The record GUID (for lookups) or option set value as string (for option sets)
 * @param fallback - The English fallback value to return if no translation exists
 * @returns The translated text, or the fallback if no translation is found
 */
export const translateValue = (
  map: TranslationMap,
  entityName: string,
  fieldName: string,
  sourceKey: string,
  fallback: string
): string => {
  if (!sourceKey) {
    return fallback;
  }
  return map[`${entityName}:${fieldName}:${sourceKey}`] ?? fallback;
};

/** CRM entity names used as translation cache keys */
export const TranslationEntity = {
  ticket: "ticket_ticket",
  product: "ticket_ticketproduct",
  productFeature: "ticket_ticketproductfeature",
} as const;

/** CRM field names used as translation cache keys */
export const TranslationField = {
  type: "ticket_type",
  severity: "ticket_severity",
  statusCode: "statuscode",
  name: "ticket_name",
} as const;

/** Maps English type labels to their CRM option set values for translation lookup */
const typeLabelToValue: Record<string, number> = {
  [TicketTypeLabel.Bug]: TicketType.bug,
  [TicketTypeLabel.FeatureRequest]: TicketType.featureRequest,
  [TicketTypeLabel.Question]: TicketType.question,
};

/** Maps English severity labels to their CRM option set values for translation lookup */
const severityLabelToValue: Record<string, number> = {
  [TicketSeverityLabel.Low]: TicketSeverity.low,
  [TicketSeverityLabel.Medium]: TicketSeverity.medium,
  [TicketSeverityLabel.High]: TicketSeverity.high,
  [TicketSeverityLabel.Critical]: TicketSeverity.critical,
};

/**
 * Translates a ticket type English label using the translation map.
 * Falls back to the original label if no translation exists.
 */
export const translateTypeLabel = (map: TranslationMap, label: string): string => {
  const value = typeLabelToValue[label];
  if (value === undefined) {
    return label;
  }
  return translateValue(map, TranslationEntity.ticket, TranslationField.type, String(value), label);
};

/**
 * Translates a ticket severity English label using the translation map.
 * Falls back to the original label if no translation exists.
 */
export const translateSeverityLabel = (map: TranslationMap, label: string): string => {
  const value = severityLabelToValue[label];
  if (value === undefined) {
    return label;
  }
  return translateValue(
    map,
    TranslationEntity.ticket,
    TranslationField.severity,
    String(value),
    label
  );
};

/** Maps English status labels to their CRM statuscode values for translation lookup */
const statusLabelToValue: Record<string, number> = {
  [TicketStatusLabel.Proposed]: TicketStatusCode.proposed,
  [TicketStatusLabel.Active]: TicketStatusCode.active,
  [TicketStatusLabel.Resolved]: TicketStatusCode.resolved,
  [TicketStatusLabel.Closed]: TicketStatusCode.closed,
};

/**
 * Translates a ticket status English label using the translation map.
 * Falls back to the original label if no translation exists.
 */
export const translateStatusLabel = (map: TranslationMap, label: string): string => {
  const value = statusLabelToValue[label];
  if (value === undefined) {
    return label;
  }
  return translateValue(
    map,
    TranslationEntity.ticket,
    TranslationField.statusCode,
    String(value),
    label
  );
};
